import type {
    Cart,
    CartDiscount,
    LineItem,
    Money,
    Order,
    ProductData,
} from "@graphql/generated/types";
import { OrderCustomFields } from "@lib/enums/CustomFields";
import type { MoneyProps } from "@ecco/ui/money";
import { getCustomField } from "../cartProductUtility";
import { getAvailability, hasSaleDiscount } from "../productUtility";
import { CustomAttributes } from "@lib/enums/ProductAttributes";
import { GIFT_WRAPPING_KEY } from "@lib/constants/product";
import { activeSiteConfig } from "@config/site/site-config";
import { mapAvailableChannels } from "@lib/product/helpers";
import { getOmnibusSubtotalAdjustmentCentAmount, isOmniBusMarket } from "../omnibus";

export type CartValidationReason =
    | "masters_empty"
    | "gift_wrapping_not_allowed"
    | "variant_missing"
    | "out_of_stock"
    | "quantity_exceeds_stock";

export type BasketValidationReason = "empty_cart" | "availability_api" | CartValidationReason;

export type BasketValidationResult =
    | { ok: true }
    | { ok: false; reason: BasketValidationReason; cartId: string; skus: string[] };

export type CartValidationResult =
    | { ok: true }
    | { ok: false; reason: CartValidationReason; skus: string[] };

export const getCartValidationResult = (
    lineItems: LineItem[],
    masters: Map<string, ProductData> | undefined
): CartValidationResult => {
    if (!masters || masters.size === 0) {
        return {
            ok: false,
            reason: "masters_empty",
            skus: lineItems.map((item) => item.variant.sku).filter(Boolean) as string[],
        };
    }

    const supplyChannelId = lineItems.findLast(({ supplyChannel }) => supplyChannel)?.supplyChannel
        ?.id;

    for (const item of lineItems) {
        const reason = getLineItemValidationFailure(item, masters, supplyChannelId);
        if (reason) {
            return {
                ok: false,
                reason,
                skus: item.variant.sku ? [item.variant.sku] : [],
            };
        }
    }

    return { ok: true };
};

export const checkCartIsValid = (
    lineItems: LineItem[],
    masters: Map<string, ProductData>
): boolean => getCartValidationResult(lineItems, masters).ok;

const getLineItemValidationFailure = (
    item: LineItem,
    masters: Map<string, ProductData>,
    supplyChannelId?: string
): CartValidationReason | null => {
    if (item.productKey === GIFT_WRAPPING_KEY) {
        return activeSiteConfig.isGiftWrappingAllowed ? null : "gift_wrapping_not_allowed";
    }

    const productData = masters.get(item.productId);
    const variant =
        productData?.variants.find(({ sku }) => sku === item.variant.sku) ??
        (productData?.masterVariant?.sku === item.variant.sku
            ? productData.masterVariant
            : undefined);

    if (!variant) return "variant_missing";

    const availableChannels = mapAvailableChannels(variant?.availability?.channels?.results);
    const availability = getAvailability(availableChannels, supplyChannelId);

    if (!availability?.isOnStock) return "out_of_stock";
    if (item.quantity > (availability?.availableQuantity ?? 0)) return "quantity_exceeds_stock";

    return null;
};

export const mapToMoneyProps = (locale: string, data?: Money): MoneyProps => {
    if (!data) return null;

    return {
        locale,
        data,
    };
};

export const getCartDiscount = (item: Cart | Order): Money => {
    return getCustomField(item, OrderCustomFields.ORDER_DISCOUNT);
};

export const editDiscountKey = (key?: string, sliceToCharacter = "-") => {
    const sliceToIndex = key?.lastIndexOf(sliceToCharacter);

    return key?.slice(0, sliceToIndex);
};

export const getAllCartDiscounts = (item: LineItem) =>
    item?.discountedPricePerQuantity?.reduce<CartDiscount[]>((acc, discount) => {
        const discounts =
            discount?.discountedPrice?.includedDiscounts
                ?.filter((item) => item.discountedAmount?.centAmount !== 0)
                ?.map((item) => item?.discount) || [];
        acc.push(...discounts);
        return acc;
    }, []) || [];

export const getProductOfferIds = (item: LineItem): string[] =>
    getCustomField(item?.price, CustomAttributes.OFFER_IDS) ?? [];

export const getCartTotalPrice = (cart: Cart): Money => {
    const hasDiscount = getCustomField(cart, OrderCustomFields.ORDER_HAS_DISCOUNT);
    const priceTag = hasDiscount
        ? OrderCustomFields.ORDER_DISCOUNTED_PRICE
        : OrderCustomFields.ORDER_PRICE;
    return getCustomField(cart, priceTag);
};

/**
 * The total the shopper sees. ORDER_PRICE is before the estimated sales tax, so sales tax markets
 * use the taxed gross, which is what Adyen and Apple Pay charge.
 */
export const getCartDisplayTotalPrice = (cart: Cart): Money =>
    activeSiteConfig.checkout.requiresSalesTaxEstimation && cart.taxedPrice?.totalGross
        ? cart.taxedPrice.totalGross
        : getCartTotalPrice(cart);

export const getCartSubTotalPrice = (item: Cart | Order): Money => {
    if (!item) return null;
    return getCustomField(item, OrderCustomFields.ORDER_PRICE);
};

// CT counts sale lines at RRP. Other markets only move lines not on sale to their selling price.
const getSummaryAdjustment = (item: Cart | Order): number => {
    if (!item) return 0;
    const lineItems = item.lineItems ?? [];
    return getOmnibusSubtotalAdjustmentCentAmount(
        isOmniBusMarket() ? lineItems : lineItems.filter((line) => !hasSaleDiscount(line.price))
    );
};

/**
 * Display only, never for payment amounts. Omnibus markets count each line at its omnibus
 * reference price, and lines not on sale count at their selling price, so pair it with
 * getCartSummaryDiscount to keep the total unchanged.
 */
export const getCartSummarySubTotalPrice = (item: Cart | Order): Money => {
    const subtotal = getCartSubTotalPrice(item);
    const adjustment = getSummaryAdjustment(item);
    if (!subtotal || !adjustment) return subtotal;
    return { ...subtotal, centAmount: subtotal.centAmount + adjustment };
};

export const getCartSummaryDiscount = (item: Cart | Order): Money => {
    const discount = getCartDiscount(item);
    const adjustment = getSummaryAdjustment(item);
    if (!adjustment) return discount;

    const centAmount = (discount?.centAmount ?? 0) + adjustment;
    const base = discount ?? getCartSubTotalPrice(item);
    return centAmount > 0 && base ? { ...base, centAmount } : null;
};

export const getUsedCartDiscountCodeIds = (cart: Cart): string[] => {
    return cart.lineItems.flatMap((item) =>
        item.discountedPricePerQuantity.flatMap((price) =>
            price.discountedPrice.includedDiscounts.map((discount) => discount.discount.id)
        )
    );
};
